import { DateRange, PropsBase, PropsRange } from '@daypicker/react'
import { isBefore, isSame, isSameOrAfter, isSameOrBefore } from 'astra'
import { useState } from 'react'

const isWithinSelectedRange = (date: Date, selected: PropsRange['selected']) => {
    if (!selected?.from || !selected.to) return false

    return !isSame(selected.from, selected.to, 'day') && isSameOrAfter(date, selected.from, 'day') && isSameOrBefore(date, selected.to, 'day')
}

const includesDisabledDate = (disabled: PropsRange['disabled'], range: DateRange) => {
    if (!Array.isArray(disabled) || !range.from || !range.to) return false

    const { from, to } = range

    return disabled.some(matcher => matcher instanceof Date && isSameOrAfter(matcher, from, 'day') && isSameOrBefore(matcher, to, 'day'))
}

const getHoverRange = (
    mode: PropsBase['mode'],
    selected: PropsRange['selected'],
    hoveredDate: Date | undefined,
    disabled: PropsRange['disabled']
): DateRange | undefined => {
    if (mode !== 'range' || !selected?.from || !hoveredDate) return undefined

    const selectedTo = selected.to ?? selected.from
    if (!isSame(selected.from, selectedTo, 'day') && isSameOrAfter(hoveredDate, selected.from, 'day') && isSameOrBefore(hoveredDate, selectedTo, 'day'))
        return undefined

    const anchor = isBefore(hoveredDate, selected.from, 'day') ? selected.from : selectedTo
    if (isSame(hoveredDate, anchor, 'day')) return undefined

    const range = isBefore(hoveredDate, anchor, 'day') ? { from: hoveredDate, to: anchor } : { from: anchor, to: hoveredDate }
    return includesDisabledDate(disabled, range) ? { from: hoveredDate, to: hoveredDate } : range
}

export const useDaySelectionHelpers = (mode: PropsBase['mode'], selected: PropsRange['selected'], disabled?: PropsRange['disabled']) => {
    const [hoveredDate, setHoveredDate] = useState<Date>()
    const hoverRange = getHoverRange(mode, selected, hoveredDate, disabled)

    const modifiers = hoverRange
        ? {
              hoverRange,
              hoverRangeStart: hoverRange.from,
              hoverRangeEnd: hoverRange.to,
          }
        : undefined

    function handleDayMouseEnter(date: Date) {
        if (mode !== 'range' || !selected?.from) return

        if (isWithinSelectedRange(date, selected)) {
            if (hoveredDate) setHoveredDate(undefined)
            return
        }

        setHoveredDate(current => (current && isSame(current, date, 'day') ? current : date))
    }

    function clearHoverRange() {
        setHoveredDate(undefined)
    }

    return { clearHoverRange, handleDayMouseEnter, modifiers }
}
